import type { VerizonReplacementDevice } from "../api/prepareVerizonReplacement";

/**
 * The Power on Activation flow for a Verizon replacement device, named as the flows' device-type buttons
 * are. `unknown` sends the expert to the manual device-type picker.
 */
export type VerizonPoaRoute = "Android Tablet" | "iOS" | "iPad" | "Pixel" | "Samsung" | "unknown";

/**
 * `make` and `model` are Horizon's asset catalog names, passed through by the backend unchanged. The
 * catalog shares a make across phones, tablets, watches and laptops, and spells models inconsistently
 * ("Galaxy Z Fold7", "Z FLIP5 5G", "GALAXY WATCH7", "Tab S7 FE 5G"), so a make alone never picks a flow.
 * Anything not matched here, including Horizon's MISC placeholders, is `unknown`: the expert picks.
 */
const IPHONE = /\biphone\b/;
const IPAD = /\bipad\b/;
const TAB = /\btab\b/;
const SAMSUNG_NOT_A_PHONE = /\b(watch|gear|book|buds)/;
const SAMSUNG_PHONE = /\bgalaxy\b|^z\s*(flip|fold)/;
const PIXEL_WATCH = /\bwatch\b/;
const PIXEL_TABLET = /\btablet\b/;
const PIXEL = /\bpixel\b/;

/** Makes whose cellular tablets are all Android, matched on a "Tab" model name. */
const ANDROID_TABLET_MAKES: ReadonlySet<string> = new Set(["orbic", "tcl"]);

const routeSamsung = (model: string): VerizonPoaRoute => {
    if (SAMSUNG_NOT_A_PHONE.test(model)) return "unknown";
    if (TAB.test(model)) return "Android Tablet";
    return SAMSUNG_PHONE.test(model) ? "Samsung" : "unknown";
};

const routeGoogle = (model: string): VerizonPoaRoute => {
    if (PIXEL_WATCH.test(model)) return "unknown";
    if (PIXEL_TABLET.test(model)) return "Android Tablet";
    return PIXEL.test(model) ? "Pixel" : "unknown";
};

const routeApple = (model: string): VerizonPoaRoute => {
    if (IPHONE.test(model)) return "iOS";
    return IPAD.test(model) ? "iPad" : "unknown";
};

/** Picks the Power on Activation flow for the replacement device `prepareVerizonReplacement` found. */
export function routeVerizonPoa(device: VerizonReplacementDevice | undefined): VerizonPoaRoute {
    if (!device) return "unknown";
    const make = device.make.trim().toLowerCase();
    const model = device.model.trim().toLowerCase();

    if (make === "apple") return routeApple(model);
    if (make === "samsung") return routeSamsung(model);
    if (make === "google") return routeGoogle(model);
    if (ANDROID_TABLET_MAKES.has(make) && TAB.test(model)) return "Android Tablet";
    return "unknown";
}
